import React, { useState } from 'react';
import OffersContainer from './container';
import {
  useGetOffersQuery,
  useAddOfferMutation,
  useUpdateOfferMutation,
  useDeleteOfferMutation,
  useChangeOfferStatusMutation
} from './services';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths } from 'date-fns';

const Offers = () => {
  // Local state for search, filters and pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectionRange, setSelectionRange] = useState({
    startDate: null,
    endDate: null,
    key: 'selection',
    color: '#000'
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  // Track ID of offer currently being status-toggled
  const [togglingId, setTogglingId] = useState(null);

  const presets = [
    {
      label: 'Current Week',
      range: {
        startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
        endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
      }
    },
    {
      label: 'Previous Week',
      range: {
        startDate: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
        endDate: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
      }
    },
    {
      label: 'Current Month',
      range: {
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date())
      }
    },
    {
      label: 'Previous Month',
      range: {
        startDate: startOfMonth(subMonths(new Date(), 1)),
        endDate: endOfMonth(subMonths(new Date(), 1))
      }
    }
  ];

  const handleButtonClick = (e) => {
    setAnchorEl(e.currentTarget);
    setPickerOpen(open => !open);
  };

  const handleSelect = (ranges) => {
    const { startDate, endDate } = ranges.selection;
    setSelectionRange({ ...ranges.selection, color: '#000' });
    setPickerOpen(false);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // RTK Query hooks (completely server-side driven)
  const { data: offersResp, isLoading, refetch } = useGetOffersQuery({
    search,
    status: statusFilter,
    type: typeFilter,
    startDate: selectionRange?.startDate ? dayjs(selectionRange.startDate).format('YYYY-MM-DD') : '',
    endDate: selectionRange?.endDate ? dayjs(selectionRange.endDate).format('YYYY-MM-DD') : '',
    page: paginationModel.page + 1,
    perPage: paginationModel.pageSize
  });
  const [addOffer, { isLoading: isAdding }] = useAddOfferMutation();
  const [updateOffer, { isLoading: isUpdating }] = useUpdateOfferMutation();
  const [deleteOffer, { isLoading: isDeleting }] = useDeleteOfferMutation();
  const [changeStatus, { isLoading: isStatusChanging }] = useChangeOfferStatusMutation();

  const isMutating = isAdding || isUpdating || isDeleting || isStatusChanging;
  const offersList = offersResp?.data?.records || [];
  const totalCount = offersResp?.data?.totalCount || 0;
  const statistics = offersResp?.data?.statistics || {};

  // Create offer handler
  const handleCreateOffer = async (payload) => {
    try {
      const resp = await addOffer(payload).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || 'Error occurred while creating offer');
    }
  };

  // Edit offer handler
  const handleEditOffer = async (payload) => {
    try {
      const resp = await updateOffer(payload).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || 'Error occurred while updating offer');
    }
  };

  // Delete offer handler
  const handleDeleteOffer = async (id) => {
    try {
      const resp = await deleteOffer(id).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || 'Error occurred while deleting offer');
    }
  };

  // Status toggle switch handler
  const handleToggleStatus = async (offer) => {
    const newStatus = offer.status === 'active' ? 'inactive' : 'active';
    setTogglingId(offer.id);
    try {
      const resp = await changeStatus({ id: offer.id, status: newStatus }).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || 'Error toggling offer status');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <OffersContainer
      offers={offersList}
      loading={isLoading}
      refetch={refetch}
      search={search}
      setSearch={setSearch}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      typeFilter={typeFilter}
      setTypeFilter={setTypeFilter}
      selectionRange={selectionRange}
      setSelectionRange={setSelectionRange}
      pickerOpen={pickerOpen}
      setPickerOpen={setPickerOpen}
      anchorEl={anchorEl}
      handleButtonClick={handleButtonClick}
      handleSelect={handleSelect}
      presets={presets}
      statistics={statistics}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      rowCount={totalCount}
      togglingId={togglingId}
      isMutating={isMutating}
      isDeleting={isDeleting}
      handleCreateOffer={handleCreateOffer}
      handleEditOffer={handleEditOffer}
      handleDeleteOffer={handleDeleteOffer}
      handleToggleStatus={handleToggleStatus}
    />
  );
};

export default Offers;
